import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

interface Package {
  id: string;
  code: string;
  name: string;
  description: string | null;
  billingType: 'MONTHLY' | 'ANNUAL' | 'ONE_TIME';
  priceINR: number;
  validityDays: number;
  rolloverPolicy: 'ROLLOVER' | 'EXPIRE';
  standardSimulationCreditsPerMonth: number;
  premiumSimulationCreditsPerMonth: number;
  addOnCreditsPerMonth: number;
  allowsOpenAITTS: boolean;
  allowsVideoAnalysis: boolean;
  maxSimulationsAllowed: number;
  allowedAIProviders: string[];
  isActive: boolean;
}

interface AddOnOrder {
  id: string;
  orderNumber: string;
  tenantId: string;
  productId: string;
  quantity: number;
  unitPriceINR: number;
  totalAmountINR: number;
  status: 'PENDING_APPROVAL' | 'COMPLETED' | 'REJECTED' | 'REFUNDED';
  metadata: any;
  createdAt: string;
}

export const PackageManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'PACKAGES' | 'ORDERS'>('PACKAGES');
  const [packages, setPackages] = useState<Package[]>([]);
  const [orders, setOrders] = useState<AddOnOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Modal State for Create/Edit Package
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<Partial<Package> | null>(null);
  const [saving, setSaving] = useState(false);

  // Approval action loading states
  const [actioningOrderId, setActioningOrderId] = useState<string | null>(null);

  const {
    currentPage: pkgPage,
    pageSize: pkgPageSize,
    totalItems: pkgTotalItems,
    paginatedItems: paginatedPackages,
    goToPage: setPkgPage,
    setPageSize: setPkgPageSize,
  } = usePagination(packages, 1, 20);

  const {
    currentPage: orderPage,
    pageSize: orderPageSize,
    totalItems: orderTotalItems,
    paginatedItems: paginatedOrders,
    goToPage: setOrderPage,
    setPageSize: setOrderPageSize,
  } = usePagination(orders, 1, 20);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      if (activeTab === 'PACKAGES') {
        const res = await api.get('/api/v1/admin/packages');
        setPackages(res.data || []);
      } else {
        const res = await api.get('/api/v1/admin/addons/orders');
        setOrders(res.data || []);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPkg({
      code: '',
      name: '',
      description: '',
      billingType: 'MONTHLY',
      priceINR: 1500,
      validityDays: 30,
      rolloverPolicy: 'ROLLOVER',
      standardSimulationCreditsPerMonth: 5,
      premiumSimulationCreditsPerMonth: 2,
      addOnCreditsPerMonth: 0,
      allowsOpenAITTS: false,
      allowsVideoAnalysis: true,
      maxSimulationsAllowed: 50,
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (pkg: Package) => {
    setEditingPkg({ ...pkg });
    setIsModalOpen(true);
  };

  const handleSavePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPkg) return;
    try {
      setSaving(true);
      setError(null);
      if (editingPkg.id) {
        await api.put(`/api/v1/admin/packages/${editingPkg.id}`, editingPkg);
        setSuccessMsg(`Package '${editingPkg.name}' updated successfully.`);
      } else {
        await api.post('/api/v1/admin/packages', editingPkg);
        setSuccessMsg(`Package '${editingPkg.name}' created successfully.`);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save package.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveOrder = async (orderId: string) => {
    try {
      setActioningOrderId(orderId);
      await api.post(`/api/v1/admin/addons/orders/${orderId}/approve`, {
        notes: 'Invoice verified and approved by Super Admin',
      });
      setSuccessMsg(`Order #${orderId} approved and credits granted.`);
      fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to approve order.');
    } finally {
      setActioningOrderId(null);
    }
  };

  const handleRejectOrder = async (orderId: string) => {
    const reason = prompt('Enter reason for order rejection:');
    if (!reason) return;
    try {
      setActioningOrderId(orderId);
      await api.post(`/api/v1/admin/addons/orders/${orderId}/reject`, { reason });
      setSuccessMsg(`Order #${orderId} rejected.`);
      fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to reject order.');
    } finally {
      setActioningOrderId(null);
    }
  };

  const handleReverseOrder = async (orderId: string) => {
    const reason = prompt('Enter refund/clawback reason:');
    if (!reason) return;
    try {
      setActioningOrderId(orderId);
      await api.post(`/api/v1/admin/addons/orders/${orderId}/reverse`, { reason });
      setSuccessMsg(`Order #${orderId} reversed and credits clawed back.`);
      fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to reverse order.');
    } finally {
      setActioningOrderId(null);
    }
  };

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px 20px 80px' }}>
      <AdminPageHeader
        title="Subscription Packages & Monetization"
        description="Configure enterprise SaaS packages, rollover policies, credit quotas, and approve pending add-on orders."
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--line)', marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTab('PACKAGES')}
          style={{
            padding: '12px 20px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'PACKAGES' ? '2px solid var(--blue)' : '2px solid transparent',
            color: activeTab === 'PACKAGES' ? 'var(--blue)' : 'var(--muted)',
            fontWeight: activeTab === 'PACKAGES' ? 600 : 500,
            cursor: 'pointer',
            fontSize: '15px',
          }}
        >
          Subscription Packages
        </button>
        <button
          onClick={() => setActiveTab('ORDERS')}
          style={{
            padding: '12px 20px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'ORDERS' ? '2px solid var(--blue)' : '2px solid transparent',
            color: activeTab === 'ORDERS' ? 'var(--blue)' : 'var(--muted)',
            fontWeight: activeTab === 'ORDERS' ? 600 : 500,
            cursor: 'pointer',
            fontSize: '15px',
          }}
        >
          Pending Add-On Approvals
          {orders.filter((o) => o.status === 'PENDING_APPROVAL').length > 0 && (
            <span
              style={{
                marginLeft: '8px',
                padding: '2px 8px',
                background: 'var(--warn-soft)',
                color: 'var(--warn)',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {orders.filter((o) => o.status === 'PENDING_APPROVAL').length}
            </span>
          )}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--bad-soft)',
            color: 'var(--bad)',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(194,59,59,0.2)',
          }}
        >
          {error}
        </div>
      )}
      {successMsg && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--ok-soft)',
            color: 'var(--ok)',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(15,138,95,0.2)',
          }}
        >
          {successMsg}
        </div>
      )}

      {/* PACKAGES TAB */}
      {activeTab === 'PACKAGES' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--ink)' }}>Active Package Plans</h3>
            <button
              onClick={handleOpenCreateModal}
              style={{
                padding: '10px 18px',
                background: 'var(--blue)',
                color: 'var(--white)',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              + Create New Package
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>Loading packages...</div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                gap: '20px',
              }}
            >
              {paginatedPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  style={{
                    background: 'var(--white)',
                    border: '1px solid var(--line)',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px', fontSize: '18px', color: 'var(--navy)' }}>{pkg.name}</h4>
                        <span style={{ fontSize: '12px', color: 'var(--faint)', fontFamily: 'monospace' }}>
                          Code: {pkg.code}
                        </span>
                      </div>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: pkg.isActive ? 'var(--ok-soft)' : 'var(--bad-soft)',
                          color: pkg.isActive ? 'var(--ok)' : 'var(--bad)',
                        }}
                      >
                        {pkg.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    <p style={{ fontSize: '14px', color: 'var(--muted)', minHeight: '40px', margin: '0 0 16px' }}>
                      {pkg.description || 'Enterprise plan with monthly simulation allocation and rollover.'}
                    </p>

                    <div style={{ margin: '0 0 16px', padding: '12px', background: 'var(--surface)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ink)' }}>
                        ₹{pkg.priceINR?.toLocaleString('en-IN')}
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--muted)' }}>
                          {' '}
                          / {pkg.billingType.toLowerCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                        Validity: {pkg.validityDays} days • Rollover: <strong>{pkg.rolloverPolicy}</strong>
                      </div>
                    </div>

                    <div style={{ fontSize: '13px', lineHeight: 1.8, color: 'var(--ink)' }}>
                      <div>
                        🎯 <strong>Standard Simulations:</strong> {pkg.standardSimulationCreditsPerMonth} / mo (3 attempts each)
                      </div>
                      <div>
                        🌟 <strong>Premium Simulations:</strong> {pkg.premiumSimulationCreditsPerMonth} / mo (10 attempts each)
                      </div>
                      <div>
                        🎙️ <strong>OpenAI Voice Eligible:</strong>{' '}
                        {pkg.allowsOpenAITTS ? (
                          <span style={{ color: 'var(--ok)', fontWeight: 600 }}>Enabled</span>
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>Standard Only</span>
                        )}
                      </div>
                      <div>
                        📹 <strong>Video Facial Analysis:</strong>{' '}
                        {pkg.allowsVideoAnalysis ? (
                          <span style={{ color: 'var(--ok)', fontWeight: 600 }}>Included</span>
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>Not Included</span>
                        )}
                      </div>
                      <div>
                        📁 <strong>Max Active Simulations:</strong> {pkg.maxSimulationsAllowed}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--line)' }}>
                    <button
                      onClick={() => handleOpenEditModal(pkg)}
                      style={{
                        width: '100%',
                        padding: '8px 14px',
                        background: 'var(--blue-soft)',
                        color: 'var(--blue)',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Edit Package Configuration
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pkgTotalItems > 0 && (
            <div style={{ marginTop: '20px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--line)', overflow: 'hidden' }}>
              <Pagination
                currentPage={pkgPage}
                totalItems={pkgTotalItems}
                pageSize={pkgPageSize}
                onPageChange={setPkgPage}
                onPageSizeChange={setPkgPageSize}
                showPageSizeSelector
                pageSizeOptions={[10, 20, 50, 100]}
                itemLabel="packages"
              />
            </div>
          )}
        </div>
      )}

      {/* ORDERS TAB */}
      {activeTab === 'ORDERS' && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--ink)' }}>Pending &amp; Processed Add-On Orders</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Trainova requires explicit Super Admin invoice confirmation before fulfilling simulation credits or voice passes.
            </p>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>Loading orders...</div>
          ) : orders.length === 0 ? (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                background: 'var(--white)',
                border: '1px solid var(--line)',
                borderRadius: '12px',
                color: 'var(--muted)',
              }}
            >
              No add-on orders found.
            </div>
          ) : (
            <div
              style={{
                background: 'var(--white)',
                border: '1px solid var(--line)',
                borderRadius: '12px',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                boxShadow: '0 1px 3px rgba(7, 29, 69, 0.05)',
              }}
            >
              <table style={{ width: '100%', minWidth: '680px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
                    <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Order #</th>
                    <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Tenant</th>
                    <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Product / Details</th>
                    <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Amount (INR)</th>
                    <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Status</th>
                    <th style={{ padding: '12px 16px', color: 'var(--navy)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((o) => (
                    <tr key={o.id} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 600, color: 'var(--ink)' }}>
                        {o.orderNumber}
                        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                          {new Date(o.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{o.tenantId}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{o.metadata?.productName || o.productId}</div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          Qty: {o.quantity} • Req by: {o.metadata?.requestedBy || 'Admin'}
                        </div>
                        {o.metadata?.notes && (
                          <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '2px' }}>
                            Notes: {o.metadata.notes}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--ink)' }}>
                        ₹{o.totalAmountINR?.toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background:
                              o.status === 'COMPLETED'
                                ? 'var(--ok-soft)'
                                : o.status === 'PENDING_APPROVAL'
                                ? 'var(--warn-soft)'
                                : 'var(--bad-soft)',
                            color:
                              o.status === 'COMPLETED'
                                ? 'var(--ok)'
                                : o.status === 'PENDING_APPROVAL'
                                ? 'var(--warn)'
                                : 'var(--bad)',
                          }}
                        >
                          {o.status}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {o.status === 'PENDING_APPROVAL' && (
                            <>
                              <button
                                disabled={actioningOrderId === o.id}
                                onClick={() => handleApproveOrder(o.id)}
                                style={{
                                  padding: '6px 12px',
                                  background: 'var(--ok)',
                                  color: 'var(--white)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                {actioningOrderId === o.id ? 'Fulfilling...' : 'Approve & Grant'}
                              </button>
                              <button
                                disabled={actioningOrderId === o.id}
                                onClick={() => handleRejectOrder(o.id)}
                                style={{
                                  padding: '6px 12px',
                                  background: 'var(--bad-soft)',
                                  color: 'var(--bad)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {o.status === 'COMPLETED' && (
                            <button
                              disabled={actioningOrderId === o.id}
                              onClick={() => handleReverseOrder(o.id)}
                              style={{
                                padding: '6px 12px',
                                background: 'var(--surface)',
                                color: 'var(--bad)',
                                border: '1px solid var(--line)',
                                borderRadius: '6px',
                                fontWeight: 600,
                                fontSize: '12px',
                                cursor: 'pointer',
                              }}
                            >
                              Refund / Clawback
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {orderTotalItems > 0 && (
            <div style={{ marginTop: '20px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--line)', overflow: 'hidden' }}>
              <Pagination
                currentPage={orderPage}
                totalItems={orderTotalItems}
                pageSize={orderPageSize}
                onPageChange={setOrderPage}
                onPageSizeChange={setOrderPageSize}
                showPageSizeSelector
                pageSizeOptions={[10, 20, 50, 100]}
                itemLabel="orders"
              />
            </div>
          )}
        </div>
      )}

      {/* CREATE / EDIT PACKAGE MODAL */}
      {isModalOpen && editingPkg && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(7, 29, 69, 0.4)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: 'var(--white)',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '640px',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '28px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: 'var(--navy)' }}>
                {editingPkg.id ? 'Edit Subscription Package' : 'Create New Subscription Package'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--muted)' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSavePackage}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Package Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={editingPkg.name || ''}
                    onChange={(e) => setEditingPkg({ ...editingPkg, name: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Package Code *
                  </label>
                  <input
                    type="text"
                    required
                    disabled={!!editingPkg.id}
                    value={editingPkg.code || ''}
                    onChange={(e) => setEditingPkg({ ...editingPkg, code: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                  Description
                </label>
                <textarea
                  rows={2}
                  value={editingPkg.description || ''}
                  onChange={(e) => setEditingPkg({ ...editingPkg, description: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Billing Interval *
                  </label>
                  <select
                    value={editingPkg.billingType}
                    onChange={(e) => setEditingPkg({ ...editingPkg, billingType: e.target.value as any })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  >
                    <option value="MONTHLY">MONTHLY</option>
                    <option value="ANNUAL">ANNUAL</option>
                    <option value="ONE_TIME">ONE_TIME</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Price (INR) *
                  </label>
                  <input
                    type="number"
                    min={0}
                    required
                    value={editingPkg.priceINR || 0}
                    onChange={(e) => setEditingPkg({ ...editingPkg, priceINR: Number(e.target.value) })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Validity (Days) *
                  </label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={editingPkg.validityDays || 30}
                    onChange={(e) => setEditingPkg({ ...editingPkg, validityDays: Number(e.target.value) })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Std Credits / Mo *
                  </label>
                  <input
                    type="number"
                    min={0}
                    required
                    value={editingPkg.standardSimulationCreditsPerMonth || 0}
                    onChange={(e) =>
                      setEditingPkg({ ...editingPkg, standardSimulationCreditsPerMonth: Number(e.target.value) })
                    }
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  />
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>3 attempts per sim</span>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Prem Credits / Mo *
                  </label>
                  <input
                    type="number"
                    min={0}
                    required
                    value={editingPkg.premiumSimulationCreditsPerMonth || 0}
                    onChange={(e) =>
                      setEditingPkg({ ...editingPkg, premiumSimulationCreditsPerMonth: Number(e.target.value) })
                    }
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  />
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>10 attempts per sim</span>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink)', marginBottom: '6px' }}>
                    Rollover Policy *
                  </label>
                  <select
                    value={editingPkg.rolloverPolicy}
                    onChange={(e) => setEditingPkg({ ...editingPkg, rolloverPolicy: e.target.value as any })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--line)' }}
                  >
                    <option value="ROLLOVER">ROLLOVER (Preserve)</option>
                    <option value="EXPIRE">EXPIRE (Reset monthly)</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '20px', padding: '16px', background: 'var(--surface)', borderRadius: '8px', marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={editingPkg.allowsOpenAITTS}
                    onChange={(e) => setEditingPkg({ ...editingPkg, allowsOpenAITTS: e.target.checked })}
                  />
                  Allow OpenAI Voice TTS
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={editingPkg.allowsVideoAnalysis}
                    onChange={(e) => setEditingPkg({ ...editingPkg, allowsVideoAnalysis: e.target.checked })}
                  />
                  Allow Video Facial Analysis
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={editingPkg.isActive}
                    onChange={(e) => setEditingPkg({ ...editingPkg, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    padding: '10px 18px',
                    background: 'var(--white)',
                    color: 'var(--ink)',
                    border: '1px solid var(--line)',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--blue)',
                    color: 'var(--white)',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {saving ? 'Saving...' : 'Save Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
